const CHECKOUT_LOADER_PATH_SUFFIX = '/bc-checkout-shop-app/bc-checkout-shop-app.js';
/** App Proxy sub-path — only used when `__BC_ROUTE_PATH__` is explicitly injected. */
export const DEFAULT_SHOPIFY_PROXY_ROUTE_PATH = '/a/sos';

export type HostingServingSurface = 'shopperos_domain' | 'shopify_app_proxy';

interface ResolveBrandclubHostDomainOptions {
  currentDocument?: Document;
  currentWindow?: Window;
  allowCurrentOriginFallback?: boolean;
}

function normalizeOrigin(value: string): string {
  return value.replace(/\/+$/, '');
}

/**
 * Back-room "routePath" knob (Storefront Concierge Plan 3 / D-14).
 *
 * When the app is served on a brand's Shopify domain under a sub-path, the proxy
 * skeleton (or an intentional Liquid inject for `servingSurface=shopify_app_proxy`)
 * sets `window.__BC_ROUTE_PATH__`. API base URLs resolved here must include it so
 * same-origin calls go through the App-Proxy forwarder instead of escaping to a
 * bare `/api-rewards/*` that the storefront does not proxy (→ Shopify 404).
 *
 * Absent → '' → front-room (script-origin / page-origin). Never invent `/a/sos`
 * from DOM layout: a classic theme embed on a Shopify custom domain (e.g.
 * petarmor.com) is still `shopperos_domain` until the operator flips the
 * serving surface and injects the route path.
 */
export function getRoutePath(): string {
  if (typeof window === 'undefined') return '';
  const raw = (window as unknown as { __BC_ROUTE_PATH__?: string }).__BC_ROUTE_PATH__;
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/+$/, '');
    if (trimmed && trimmed !== '/') {
      return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    }
  }
  return '';
}

/**
 * True when the app is served on a brand's Shopify domain under the App-Proxy
 * sub-path (back-room). Mirrors landing-page `utils/routePath.ts#isBackRoom`
 * — same `__BC_ROUTE_PATH__` signal via `getRoutePath()`. Used by concierge
 * login prompts as the defensive client-side gate (Plan 7 C3/C4): no phone/OTP
 * UI ever renders on-store, even before the surface-aware backend deploys.
 */
export function isBackRoom(): boolean {
  return getRoutePath() !== '';
}

/**
 * Shopify App Proxy SSO / reverse-sync is allowed when we are on the proxy
 * sub-path (explicit inject) OR the brand's config says the consumer surface
 * is Shopify. Layout heuristics (cross-origin checkout script) are NOT enough —
 * that pattern is shared by every classic theme embed, including brands still
 * on `shopperos_domain`.
 */
export function isShopifyAppProxySurface(servingSurface?: HostingServingSurface | null): boolean {
  return isBackRoom() || servingSurface === 'shopify_app_proxy';
}

/**
 * Route path for App Proxy leaf URLs (`/shopify-login`, etc.) once
 * {@link isShopifyAppProxySurface} is true. Prefer the explicit inject; fall
 * back to the default sub-path when config says Shopify surface but Liquid
 * has not been re-injected yet (D-27 bubble without `__BC_ROUTE_PATH__`).
 */
export function getShopifyAppProxyRoutePath(servingSurface?: HostingServingSurface | null): string {
  const injected = getRoutePath();
  if (injected) return injected;
  if (servingSurface === 'shopify_app_proxy') return DEFAULT_SHOPIFY_PROXY_ROUTE_PATH;
  return '';
}

export function isShopifyPreviewHost(host?: string | null): boolean {
  return typeof host === 'string' && host.toLowerCase().endsWith('.shopifypreview.com');
}

export function isShopifyStorefrontHost(host?: string | null): boolean {
  return typeof host === 'string' && host.toLowerCase().endsWith('.myshopify.com');
}

export function isEmbeddedCheckoutScriptUrl(scriptSrc?: string | null): boolean {
  if (!scriptSrc) {
    return false;
  }

  try {
    const url = new URL(scriptSrc);
    // The embedded checkout loader script is expected to carry a brand/app id
    // query param (e.g. `...?id=gund`). This distinguishes the storefront
    // embed from direct/static script hosting.
    return url.pathname.endsWith(CHECKOUT_LOADER_PATH_SUFFIX) && url.searchParams.has('id');
  } catch {
    return false;
  }
}

export function findEmbeddedCheckoutScript(currentDocument?: Document): HTMLScriptElement | null {
  if (!currentDocument) {
    return null;
  }

  const scripts = Array.from(currentDocument.getElementsByTagName('script'));
  return scripts.find((script) => isEmbeddedCheckoutScriptUrl(script.src)) ?? null;
}

export function resolveBrandclubHostDomain(options: ResolveBrandclubHostDomainOptions = {}): string {
  const currentDocument = options.currentDocument ?? (typeof document !== 'undefined' ? document : undefined);
  const currentWindow = options.currentWindow ?? (typeof window !== 'undefined' ? window : undefined);
  const routePath = getRoutePath();

  // Back-room Shopify surface: API/redeem must be same-origin through the App-Proxy
  // forwarder even when the checkout bundle script is hosted on a cross-origin CDN.
  if (routePath && currentWindow?.location?.origin) {
    return `${normalizeOrigin(currentWindow.location.origin)}${routePath}`;
  }

  const embeddedCheckoutScript = findEmbeddedCheckoutScript(currentDocument);

  if (embeddedCheckoutScript?.src) {
    return `${normalizeOrigin(new URL(embeddedCheckoutScript.src).origin)}${routePath}`;
  }

  if (!currentWindow || options.allowCurrentOriginFallback === false) {
    throw new Error('[web-app] Could not resolve host domain from the current runtime context.');
  }

  const currentHostname = currentWindow.location?.hostname;

  if (!currentHostname || isShopifyStorefrontHost(currentHostname) || isShopifyPreviewHost(currentHostname)) {
    throw new Error(
      '[web-app] Could not resolve host domain for an embedded Shopify storefront. Ensure the checkout bundle is loaded correctly.',
    );
  }

  // Back-room: append the injected routePath so API callers (which suffix
  // `/api-rewards/...`) hit the same-origin forwarder. Front-room: '' → unchanged.
  return `${normalizeOrigin(currentWindow.location.origin)}${routePath}`;
}

export function getAppIdFromHostDomain(hostDomain: string): string {
  return normalizeOrigin(hostDomain).replace(/^https?:\/\//, '');
}
